/**
 * TabPicker UI Modal Component
 * 
 * Modal dialog component for overlays and important user interactions.
 * Provides drawer and centered modal variants with backdrop and animations.
 * 
 * @module TabPicker.UI.Modal
 */

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Modal component props interface
 * 
 * @interface ModalProps
 * @property {boolean} isOpen - Whether the modal is open
 * @property {() => void} onClose - Callback when modal should close
 * @property {string} [title] - Modal title
 * @property {React.ReactNode} children - Modal content
 * @property {'center' | 'drawer'} [variant] - Modal display variant
 * @property {'sm' | 'md' | 'lg' | 'xl' | 'full'} [size] - Modal size
 * @property {boolean} [closeOnBackdrop] - Close modal when backdrop is clicked
 * @property {boolean} [closeOnEscape] - Close modal when Escape key is pressed
 */
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  variant?: 'center' | 'drawer';
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
}

/**
 * Modal component for dialogs and overlays
 * 
 * Displays content in a modal overlay with backdrop. Supports centered
 * modal and bottom drawer variants with multiple sizes.
 * 
 * @param {ModalProps} props - Component props
 * @returns {JSX.Element | null} Modal element or null if closed
 * 
 * @example
 * // Centered modal
 * <Modal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Confirm Action"
 * >
 *   <p>Are you sure you want to proceed?</p>
 *   <ModalFooter>
 *     <Button variant="secondary" onClick={() => setIsOpen(false)}>Cancel</Button>
 *     <Button onClick={handleConfirm}>Confirm</Button>
 *   </ModalFooter>
 * </Modal>
 * 
 * @example
 * // Drawer variant
 * <Modal
 *   isOpen={showImport}
 *   onClose={() => setShowImport(false)}
 *   variant="drawer"
 *   title="Import Data"
 * >
 *   <ImportForm />
 * </Modal>
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  variant = 'center',
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);
  
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose();
    }
  };
  
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4'
  };
  
  const content = (
    <div className={`
      ${variant === 'center' 
        ? `${sizeClasses[size]} w-full rounded-2xl` 
        : 'w-full max-w-2xl rounded-t-2xl tablet:rounded-2xl'}
      bg-white shadow-2xl relative z-50
    `}>
      {title && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl leading-none p-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}
      <div className="p-4">
        {children}
      </div>
    </div>
  );
  
  return createPortal(
    <div 
      className={`
        fixed inset-0 z-40 flex
        ${variant === 'center' 
          ? 'items-center justify-center p-4' 
          : 'items-end tablet:items-center tablet:justify-center'}
      `}
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black/40" />
      {content}
    </div>,
    document.body
  );
}

/**
 * Modal header component props
 */
export interface ModalHeaderProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * Modal header component
 * 
 * @param {ModalHeaderProps} props - Component props
 * @returns {JSX.Element} Modal header element
 */
export function ModalHeader({ className = '', children }: ModalHeaderProps) {
  return (
    <div className={`pb-4 ${className}`}>
      {children}
    </div>
  );
}

/**
 * Modal body component props
 */
export interface ModalBodyProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * Modal body component
 * 
 * @param {ModalBodyProps} props - Component props
 * @returns {JSX.Element} Modal body element
 */
export function ModalBody({ className = '', children }: ModalBodyProps) {
  return (
    <div className={`py-4 ${className}`}>
      {children}
    </div>
  );
}

/**
 * Modal footer component props
 */
export interface ModalFooterProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * Modal footer component
 * 
 * @param {ModalFooterProps} props - Component props
 * @returns {JSX.Element} Modal footer element
 */
export function ModalFooter({ className = '', children }: ModalFooterProps) {
  return (
    <div className={`pt-4 mt-4 border-t border-gray-200 flex items-center justify-end gap-2 ${className}`}>
      {children}
    </div>
  );
}